'use client';

import { CompositeItem } from '@floating-ui/react';
import { interactionResponse } from '@zillow/yield-callback';
import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';
import { AccordionItemContext } from './context/accordion-item.context';
import { AccordionRootActionsContext } from './context/accordion-root.context';
import { accordionHeaderTriggerRecipe } from './styles/accordion-header-trigger.styles';

import type { AccordionHeaderTriggerPropsInterface } from './types/accordion-header-trigger.types';

/**
 * Toggle button for `Accordion.Header`.
 */
export const AccordionHeaderTrigger = forwardRef<
  HTMLButtonElement,
  AccordionHeaderTriggerPropsInterface
>((props: AccordionHeaderTriggerPropsInterface, forwardedRef) => {
  const { className, css: cssProp, onClick, 'aria-label': ariaLabel, ...rest } = props;

  const { disabled, expanded, value } = useContext(AccordionItemContext);
  const { toggleExpanded, shouldAwaitInteractionResponse } = useContext(
    AccordionRootActionsContext,
  );

  const styles = css(accordionHeaderTriggerRecipe.raw({ expanded }), cssProp);

  const headerContentId = `${value}-header`;
  const resolvedLabelledBy = ariaLabel ? undefined : headerContentId;

  const onAccordionHeaderClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    async (event) => {
      if (disabled) return;

      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      toggleExpanded?.(value);
      onClick?.(event);
    },
    [disabled, onClick, value, toggleExpanded, shouldAwaitInteractionResponse],
  );

  return (
    <CompositeItem
      {...rest}
      render={(compositeProps) => {
        /*
         * Drop the composite's roving-focus markers. Every header button stays
         * tabbable per APG, and `data-active` would otherwise make Panda's
         * `_active` pressed styles stick for the focused item.
         */
        const {
          'data-active': _compositeDataActive,
          tabIndex: _compositeTabIndex,
          ...restCompositeProps
        } = compositeProps as typeof compositeProps & { 'data-active'?: string };

        return (
          <button
            {...restCompositeProps}
            ref={forwardedRef}
            type="button"
            data-c11n-component="Accordion.HeaderTrigger"
            data-c11n-version={__C11N_VERSION__}
            className={cx('c11n', 'c11n_accordion__header-trigger', styles, className)}
            aria-expanded={expanded}
            aria-controls={`${value}-panel`}
            aria-label={ariaLabel}
            aria-labelledby={resolvedLabelledBy}
            disabled={disabled}
            onClick={onAccordionHeaderClick}
          />
        );
      }}
    />
  );
});

AccordionHeaderTrigger.displayName = 'Accordion.HeaderTrigger';
